/**
 * Composable for handling availability classification and related functionality
 */

export interface AvailabilityClassification {
  variant: 'error' | 'hint' | 'success' | 'warning';
  label: string;
}

export const AVAILABILITY_THRESHOLDS = {
  VERY_LOW: 30,
  LOW: 60,
  STANDARD: 120,
} as const;

export const AVAILABILITY_VARIANTS = {
  ERROR: 'error',
  HINT: 'hint',
  SUCCESS: 'success',
  WARNING: 'warning',
} as const;

export const AVAILABILITY_LABELS = {
  NO_DATA: 'No data',
  NO_AVAILABILITIES: 'No availabilities',
  VERY_LOW: 'Very Low',
  LOW: 'Low',
  STANDARD: 'Standard',
  HIGH: 'High',
} as const;

/**
 * Get availability classification based on count
 * @param count - The availability count
 * @returns Object containing variant and label for the badge
 */
export function getAvailabilityClassification(
  count: number | null | undefined,
): AvailabilityClassification {
  if (count === null || count === undefined) {
    return {
      variant: AVAILABILITY_VARIANTS.WARNING,
      label: AVAILABILITY_LABELS.NO_DATA,
    };
  }

  if (count === 0) {
    return {
      variant: AVAILABILITY_VARIANTS.ERROR,
      label: AVAILABILITY_LABELS.NO_AVAILABILITIES,
    };
  }

  if (count < AVAILABILITY_THRESHOLDS.VERY_LOW) {
    return {
      variant: AVAILABILITY_VARIANTS.HINT,
      label: AVAILABILITY_LABELS.VERY_LOW,
    };
  }

  if (
    count >= AVAILABILITY_THRESHOLDS.VERY_LOW &&
    count < AVAILABILITY_THRESHOLDS.LOW
  ) {
    return {
      variant: AVAILABILITY_VARIANTS.HINT,
      label: AVAILABILITY_LABELS.LOW,
    };
  }

  if (
    count >= AVAILABILITY_THRESHOLDS.LOW &&
    count <= AVAILABILITY_THRESHOLDS.STANDARD
  ) {
    return {
      variant: AVAILABILITY_VARIANTS.SUCCESS,
      label: AVAILABILITY_LABELS.STANDARD,
    };
  }

  // Over 120
  return {
    variant: AVAILABILITY_VARIANTS.SUCCESS,
    label: AVAILABILITY_LABELS.HIGH,
  };
}

/**
 * Composable for availability-related functionality
 */
export function useAvailabilities() {
  return {
    getAvailabilityClassification,
    AVAILABILITY_THRESHOLDS,
    AVAILABILITY_VARIANTS,
    AVAILABILITY_LABELS,
  };
}
